import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Bike } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { vehicleTypes } from "../../data/content";
import { createDoc, uploadImage, logActivity } from "../../lib/store";
import { Field, FileDrop } from "./PartnerForm";

const inputCls =
  "w-full rounded-xl border border-[#0033FF]/12 bg-white px-4 py-3 text-[#0b1221] placeholder:text-[#9aa7bd] focus:outline-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent transition-shadow duration-200";

const empty = {
  fullName: "",
  mobile: "",
  whatsapp: "",
  address: "",
  deliveryArea: "",
  vehicleType: "",
};

export const RiderForm = () => {
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState(null);
  const [idProof, setIdProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.mobile || !form.vehicleType) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.mobile)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    setLoading(true);
    try {
      const [photoUrl, idProofUrl] = await Promise.all([
        photo ? uploadImage(photo, "riders/photos") : Promise.resolve(""),
        idProof ? uploadImage(idProof, "riders/id-proofs") : Promise.resolve(""),
      ]);
      await createDoc("riders", {
        ...form,
        photoUrl,
        idProofUrl,
        status: "pending",
        notes: "",
      });
      await logActivity("rider", `New rider application from ${form.fullName}`);
      setDone(true);
      setForm(empty);
      setPhoto(null);
      setIdProof(null);
      toast.success("Application submitted successfully! Our team will reach out soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="rider"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="rider-section"
    >
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0033FF]/8 blur-3xl" />
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 order-2 lg:order-1 bg-white rounded-3xl p-8 md:p-10 border border-[#0033FF]/8 shadow-[0_20px_60px_rgba(0,51,255,0.08)] grid sm:grid-cols-2 gap-5"
            data-testid="rider-form"
          >
            <Field label="Full Name *">
              <input className={inputCls} value={form.fullName} onChange={set("fullName")} placeholder="Full name" data-testid="rider-fullName" />
            </Field>
            <Field label="Mobile Number *">
              <input className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91" data-testid="rider-mobile" />
            </Field>
            <Field label="WhatsApp Number">
              <input className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="+91" data-testid="rider-whatsapp" />
            </Field>
            <Field label="Delivery Area">
              <input className={inputCls} value={form.deliveryArea} onChange={set("deliveryArea")} placeholder="e.g. Krishnanagar" data-testid="rider-area" />
            </Field>
            <Field label="Address" full>
              <input className={inputCls} value={form.address} onChange={set("address")} placeholder="Your current address" data-testid="rider-address" />
            </Field>
            <Field label="Vehicle Type *">
              <Select value={form.vehicleType} onValueChange={(v) => setForm((f) => ({ ...f, vehicleType: v }))}>
                <SelectTrigger className={inputCls + " h-[50px]"} data-testid="rider-vehicle">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleTypes.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Upload Photo">
              <FileDrop file={photo} onChange={setPhoto} testid="rider-photo" accept="image/*" />
            </Field>
            <Field label="Upload ID Proof">
              <FileDrop file={idProof} onChange={setIdProof} testid="rider-idproof" accept="image/*,application/pdf" />
            </Field>

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] text-white font-semibold py-4 rounded-xl hover:bg-[#0026bf] transition-colors duration-200 disabled:opacity-70"
                data-testid="rider-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : done ? <CheckCircle2 className="w-5 h-5" /> : null}
                {loading ? "Submitting..." : done ? "Submitted" : "Join as a Rider"}
              </button>
            </div>
          </motion.form>

          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-28">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-[#0033FF] items-center justify-center text-white mb-6">
              <Bike className="w-7 h-7" />
            </span>
            <p className="text-sm font-semibold text-[#0033FF] uppercase tracking-widest mb-3">
              Become a Rider
            </p>
            <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-[#0b1221]">
              Ride with us. Earn on your schedule.
            </h2>
            <p className="mt-5 text-[#5e6c84] text-lg leading-relaxed">
              Flexible hours, weekly payouts, and a supportive team. Sign up in
              minutes and start earning across Agartala.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
